import { createContext, useContext, useEffect, useState } from 'react';
import { LLMPatientInformation } from './LLMDiagnoseContext';
import { useAuthUser } from './AuthUserContext';
import { getCurrentUser } from 'aws-amplify/auth';
import { awsConfig } from '../config/aws.config';

type Session = {
  created_at: string;
  updated_at: string;
  type: string;
  data: {
    patient: LLMPatientInformation;
    notes: string;
    case_id?: string; // Optional, as it may not always be present
    attachment_ids?: string[]; // Attachment IDs created at session time
  };
  id: string;
  access_token?: string; // Add access_token field
};

type DiagnosticsComponentContextType = {
  sessionId: string;
  setSessionId: (sessionId: string) => void;
  session: Session | null;
  setSession: (session: Session | null) => void;
  signature: string;
  setSignature: (signature: string) => void;
  timestamp: string;
  setTimestamp: (timestamp: string) => void;
};

const DiagnosticsComponentContext =
  createContext<DiagnosticsComponentContextType>({
    sessionId: '',
    setSessionId: () => {},
    session: null,
    setSession: () => {},
    signature: '',
    setSignature: () => {},
    timestamp: '',
    setTimestamp: () => {},
  });

export const DiagnosticsComponentProvider = ({
  children,
}: React.PropsWithChildren) => {
  const [sessionId, setSessionId] = useState('');
  const [timestamp, setTimestamp] = useState('');
  const [signature, setSignature] = useState('');
  const [session, setSession] = useState<Session | null>(null);
  const { user } = useAuthUser();

  const value = {
    sessionId,
    setSessionId,
    session,
    setSession,
    timestamp,
    setTimestamp,
    signature,
    setSignature,
  };

  // Function to redirect to Cognito login with state preservation
  const redirectToLogin = () => {
    // Store current URL in sessionStorage to return to after login
    const currentUrl = window.location.href;

    // For components mode, we rely on the state parameter
    // For regular app mode, we also store in sessionStorage as backup
    if (process.env.REACT_APP_ENTRYPOINT !== 'components') {
      window.sessionStorage.setItem('gekko/returnUrl', currentUrl);
    }

    // Build Cognito login URL with required scopes
    const loginUrl =
      `https://${awsConfig.IDP_DOMAIN}/oauth2/authorize?` +
      `client_id=${awsConfig.USER_POOL_CLIENT_ID}` +
      `&response_type=code` +
      `&scope=openid%20email%20profile%20aws.cognito.signin.user.admin%20gekkovet/read%20gekkovet/write%20gekkovet/user.write%20gekkovet/user.read` +
      `&redirect_uri=${encodeURIComponent(awsConfig.REDIRECT_SIGN_IN || '')}` +
      `&state=${encodeURIComponent(currentUrl)}`;

    window.location.href = loginUrl;
  };

  // Check for embedded tokens in sessionStorage (set by initComponents)
  const getEmbeddedTokens = () => {
    const embeddedTokensString = window.sessionStorage.getItem(
      'gekko/embeddedTokens'
    );
    if (embeddedTokensString) {
      try {
        return JSON.parse(embeddedTokensString);
      } catch (error) {
        console.error('Failed to parse embedded tokens:', error);
      }
    }
    return null;
  };

  // Check if we're running in an iframe
  const isInIframe = () => {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  };

  useEffect(() => {
    if (!sessionId) return;
    console.log(
      'DiagnosticsComponentContext: Fetching session for sessionId:',
      sessionId
    );
    setSession(null);
    fetch(`${awsConfig.COMPONENTS_API_URL}/api/v1/sessions/${sessionId}`)
      .then((response) => response.json())
      .then(async (data) => {
        setSession(data);
        // Only set auth header if we have valid signature and timestamp (not dummy values)
        if (
          signature &&
          timestamp &&
          signature !== 'dummy' &&
          timestamp !== 'dummy'
        ) {
          window.sessionStorage.setItem(
            'gekko/authheader',
            `GekkoComponents ${sessionId};${signature};${timestamp}`
          );
        }
        (window as any).__componentsCallback('sessionLoaded', data);
        console.log(
          'DiagnosticsComponentContext: Session data received:',
          data
        );
        // If we have valid GekkoComponents signature auth, no further auth check needed
        if (
          signature &&
          timestamp &&
          signature !== 'dummy' &&
          timestamp !== 'dummy'
        ) {
          return;
        }
        // Check if session has access_token
        if (!data.access_token) {
          console.log(
            'DiagnosticsComponentContext: No access_token in session, checking authentication'
          );

          // If we're in an iframe and have embedded tokens, use them
          if (isInIframe() && getEmbeddedTokens()) {
            console.log(
              'DiagnosticsComponentContext: Using embedded tokens from parent window'
            );
            // Proceed with session using embedded tokens
            setSession(data);
            // Embedded tokens are already stored by initComponents
            (window as any).__componentsCallback('sessionLoaded', data);
            return;
          }

          // Check if we have a logged in Cognito user (fallback)
          try {
            const cognitoUser = await getCurrentUser();
            console.log(
              'DiagnosticsComponentContext: Cognito user found:',
              cognitoUser
            );
            // User is logged in, proceed with session
            setSession(data);
            (window as any).__componentsCallback('sessionLoaded', data);
          } catch (error: any) {
            // No logged in user and no embedded tokens
            if (isInIframe()) {
              // In iframe but no tokens yet - send message to parent requesting auth
              console.log(
                'DiagnosticsComponentContext: In iframe but no auth tokens, requesting from parent'
              );
              window.parent.postMessage(
                {
                  type: 'GEKKO_REQUEST_AUTH',
                  payload: { sessionId },
                },
                '*'
              );
            } else {
              // Not in iframe, redirect to login
              console.log(
                'DiagnosticsComponentContext: No authenticated user found, redirecting to login',
                error
              );
              redirectToLogin();
            }
          }
        } else {
          console.log(
            'DiagnosticsComponentContext: Session has access_token, proceeding normally'
          );
          // Session has access_token, proceed normally
          setSession(data);
          (window as any).__componentsCallback('sessionLoaded', data);
        }
      })
      .catch((error) => {
        console.error(
          'DiagnosticsComponentContext: Error fetching session:',
          error
        );
      });
  }, [sessionId, signature, timestamp]);

  // Check for return URL after authentication
  useEffect(() => {
    if (
      user.isAuthenticated &&
      process.env.REACT_APP_ENTRYPOINT !== 'components'
    ) {
      const returnUrl = window.sessionStorage.getItem('gekko/returnUrl');
      if (returnUrl && returnUrl !== window.location.href) {
        window.sessionStorage.removeItem('gekko/returnUrl');
        // Only redirect if we're not already at the return URL
        window.location.href = returnUrl;
      }
    }
  }, [user.isAuthenticated]);

  return (
    <DiagnosticsComponentContext.Provider value={value}>
      {children}
    </DiagnosticsComponentContext.Provider>
  );
};

export const useDiagnosticsComponentContext = () => {
  return useContext(DiagnosticsComponentContext);
};
